// src/pages/AdminAgendaCitas/components/AppointmentScheduler.tsx
import React, { useRef, useState, useMemo, useEffect } from 'react';
import Scheduler, { View } from 'devextreme-react/scheduler';
import Tooltip from 'devextreme-react/tooltip';
import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import { AppointmentData } from '../../../types/types.agendaCitas';
import './../AdminAgendaCitas.scss';
import { locale, loadMessages } from 'devextreme/localization';
import esMessages from 'devextreme/localization/messages/es.json';
import { newToast } from '../../../components';
import { useScreenSize } from '../../../utils/media-query';

// Cargar los mensajes en español
loadMessages(esMessages);

// Establecer el locale a español
locale('es');

// Función para formatear fechas con día de la semana
const formatDateWithDay = (date: Date, view: string, isSmallScreen: boolean = false) => {
  // Si es pantalla pequeña, usar formato corto estándar
  if (isSmallScreen) {
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Formato personalizado para pantallas grandes
  const dayNamesShort = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const monthNamesShort = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
  
  const dayName = dayNamesShort[date.getDay()];
  const day = date.getDate();
  const month = monthNamesShort[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2); // Solo últimos 2 dígitos
  
  switch (view) {
    case 'day':
      return `${dayName} ${day} de ${month} del ${year}`;
    case 'workWeek':
    case 'week':
      // Para vista semanal, mostrar rango con días
      const endDate = new Date(date);
      if (view === 'workWeek') {
        // Semana laboral: lunes a viernes
        endDate.setDate(date.getDate() + 4);
      } else {
        // Semana completa: 6 días
        endDate.setDate(date.getDate() + 6);
      }
      
      const endDayName = dayNamesShort[endDate.getDay()];
      const endDay = endDate.getDate();
      const endMonth = monthNamesShort[endDate.getMonth()];
      const endYear = endDate.getFullYear().toString().slice(-2);
      
      if (date.getMonth() === endDate.getMonth() && date.getFullYear() === endDate.getFullYear()) {
        // Mismo mes y año
        return `${dayName} ${day} al ${endDayName} ${endDay} de ${month} del ${year}`;
      } else if (date.getFullYear() === endDate.getFullYear()) {
        // Mismo año, diferente mes
        return `${dayName} ${day} de ${month} al ${endDayName} ${endDay} de ${endMonth} del ${year}`;
      } else {
        // Diferente año
        return `${dayName} ${day} de ${month} del ${year} al ${endDayName} ${endDay} de ${endMonth} del ${endYear}`;
      }
    case 'month':
      const monthNamesFull = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      return `${monthNamesFull[date.getMonth()]} del ${year}`;
    default:
      return `${dayName} ${day} de ${month} del ${year}`;
  }
};

interface AppointmentSchedulerProps {
  appointments: AppointmentData[];
  currentDate: Date;
  startDayHour: number;
  endDayHour: number;
  cellDuration: number;
  onAppointmentFormOpening: (appointmentData: any, isNew: boolean) => void;
  onViewOrDateChange?: (start: Date, end: Date) => void;
  onAppointmentUpdated?: (e: any) => void;
}

// Opciones para el filtro de búsqueda
const searchFilterOptions = [
  { value: 'all', text: 'Buscar en todo' },
  { value: 'title', text: 'Paciente' },
  { value: 'documento', text: 'Documento' },
];

// Componente de barra de búsqueda personalizada
const SearchToolbar = ({ searchText, onSearchChange, searchFilter, onSearchFilterChange, onClearSearch, appointmentsCount }: { searchText: string; onSearchChange: (value: string) => void; searchFilter: string; onSearchFilterChange: (value: string) => void; onClearSearch: () => void; appointmentsCount: number }) => {
  return (
    <div
      className='scheduler-search-toolbar'
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 0',
        borderBottom: '1px solid #e0e0e0',
        marginBottom: '10px',
        flexWrap: 'wrap',
      }}>
      <div
        className='search-controls'
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flex: '1',
          minWidth: '280px',
          flexWrap: 'wrap',
        }}>
        <SelectBox dataSource={searchFilterOptions} value={searchFilter} onValueChanged={(e) => onSearchFilterChange(e.value)} displayExpr='text' valueExpr='value' width={150} placeholder='Filtro' stylingMode='outlined' />
        <TextBox value={searchText} onValueChanged={(e) => onSearchChange(e.value || '')} placeholder='Buscar citas...' mode='search' width={200} showClearButton={true} stylingMode='outlined' />        
      </div>

      <div
        className='appointments-counter'
        style={{
          fontSize: '14px',
          color: '#666',
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
        }}>
        {appointmentsCount} cita{appointmentsCount !== 1 ? 's' : ''} encontrada{appointmentsCount !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

// Componente para el contenido del tooltip
const AppointmentTooltipContent = ({ data, onClose }: { data: AppointmentData | null; onClose: () => void }) => {
  if (!data) return null;

  // Determinar el texto del estado basado en el valor numérico
  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return 'Programada';
      case 2:
        return 'Completada';
      case 3:
        return 'Cancelada';
      case 4:
        return 'No Asistió';
      default:
        return 'Desconocido';
    }
  };

  // Función separada para copiar
  const handleCopy = () => {
    if (data) {
      const textToCopy = `${data.documento} - ${data.title}`;
      navigator.clipboard.writeText(textToCopy);
      newToast('SUCCESS', 'Texto copiado al portapapeles', 5);
    }
  };

  return (
    <div className='appointment-tooltip' style={{ maxWidth: '90vw', width: '90%', boxSizing: 'border-box', wordBreak: 'break-word', position: 'relative' }}>
      {/* Botón X para cerrar */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          color: '#666',
          padding: '0',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          zIndex: 1,
        }}
        title='Cerrar'>
        <i className='dx-icon dx-icon-close'></i>
      </button>

      <div className='tooltip-header'>
        <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', margin: 0, paddingRight: '25px' }}>
          {data.documento} - {data.title}
          <span onClick={handleCopy} title='Copiar' style={{ cursor: 'pointer', marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
            <i className='dx-icon dx-icon-copy' style={{ fontSize: '18px' }}></i>
          </span>
        </h3>
      </div>
      <div className='tooltip-time'>
        <i className='dx-icon dx-icon-clock' style={{ fontSize: '14px' }}></i>
        <span>
          {new Date(data.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -{new Date(data.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      {data.description && (
        <div
          className='tooltip-description'
          style={{
            maxHeight: '120px',
            overflowY: 'auto',
            wordBreak: 'break-word',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            boxSizing: 'border-box',
            maxWidth: '100%',
            whiteSpace: 'pre-line',
            textAlign: 'left',
          }}>
          <i className='dx-icon dx-icon-comment' style={{ fontSize: '14px' }}></i>
          <span>{data.description}</span>
        </div>
      )}
      <div className='tooltip-time'>Usuario: {data.usuario}</div>
      <div className='tooltip-time'>
        Estado:<span className={`status-badge status-${data.status}`}>{getStatusText(data.status)}</span>
      </div>
    </div>
  );
};

const AppointmentScheduler = React.forwardRef<Scheduler, AppointmentSchedulerProps>(({ appointments, currentDate, startDayHour, endDayHour, cellDuration, onAppointmentFormOpening, onViewOrDateChange, onAppointmentUpdated }, ref) => {
  //const schedulerRef = useRef<Scheduler>(null);
  const tooltipRef = useRef<any>(null);
  const searchToolbarRef = useRef<HTMLDivElement>(null);
  const schedulerWrapperRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<AppointmentData | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipTarget, setTooltipTarget] = useState<any>(null);
  const [schedulerHeight, setSchedulerHeight] = useState('calc(100vh - 260px)');
  const {isMedium, isLarge, isSmall, isXSmall} = useScreenSize();

  // Estados para la búsqueda
  const [searchText, setSearchText] = useState('');
  const [searchFilter, setSearchFilter] = useState('all');
  const [currentView, setCurrentView] = useState('workWeek');

  // Efecto para calcular la altura dinámica del scheduler
  useEffect(() => {
    const calculateSchedulerHeight = () => {
      if (searchToolbarRef.current && schedulerWrapperRef.current) {
        const toolbar = searchToolbarRef.current;
        const toolbarHeight = toolbar.offsetHeight;

        // Obtener altura de viewport
        const viewportHeight = window.innerHeight;

        // Calcular offset del contenedor desde el top
        const wrapperRect = schedulerWrapperRef.current.getBoundingClientRect();
        const offsetFromTop = wrapperRect.top;

        // Calcular altura disponible considerando:
        // - Altura del viewport
        // - Offset desde arriba
        // - Altura de la toolbar de búsqueda
        // - Padding/margin adicional (aproximadamente 40px)
        const availableHeight = viewportHeight - offsetFromTop - toolbarHeight - 40;

        // Asegurar altura mínima
        const minHeight = 300;
        const finalHeight = Math.max(availableHeight, minHeight);

        setSchedulerHeight(`${finalHeight}px`);
      }
    };

    // Calcular altura inicial
    calculateSchedulerHeight();

    // Recalcular en resize
    const handleResize = () => {
      calculateSchedulerHeight();
    };

    window.addEventListener('resize', handleResize);

    // También recalcular después de un pequeño delay para asegurar que el DOM esté listo
    const timeoutId = setTimeout(calculateSchedulerHeight, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [searchText, searchFilter]); // Recalcular cuando cambien los filtros

  // Filtrar citas basado en la búsqueda
  const filteredAppointments = useMemo(() => {
    if (!searchText.trim()) {
      return appointments;
    }

    const searchLower = searchText.toLowerCase().trim();

    return appointments.filter((appointment) => {
      switch (searchFilter) {
        case 'title':
          return appointment.title?.toLowerCase().includes(searchLower);
        case 'documento':
          return appointment.documento?.toLowerCase().includes(searchLower);
        case 'description':
          return appointment.description?.toLowerCase().includes(searchLower);
        case 'usuario':
          return appointment.usuario?.toLowerCase().includes(searchLower);
        case 'all':
        default:
          return appointment.title?.toLowerCase().includes(searchLower) || appointment.documento?.toLowerCase().includes(searchLower) || appointment.description?.toLowerCase().includes(searchLower) || appointment.usuario?.toLowerCase().includes(searchLower);
      }
    });
  }, [appointments, searchText, searchFilter]);

  // Función para limpiar la búsqueda
  const handleClearSearch = () => {
    setSearchText('');
    setSearchFilter('all');
  };

  // Función para cerrar el tooltip
  const closeTooltip = () => {
    setTooltipVisible(false);
    setTooltipData(null);
    setTooltipTarget(null);
  };
  // Personalizar el renderizado de las citas
  const onAppointmentRendered = (e: any) => {
    // Aplicar color basado en el estado de la cita
    if (e.appointmentData.color) {
      e.appointmentElement.style.backgroundColor = e.appointmentData.color;
    }

    // Personalizar el contenido de la cita
    const appointmentContent = e.appointmentElement.querySelector('.dx-scheduler-appointment-content');
    if (appointmentContent) {
      // Formatear la hora
      const startTime = new Date(e.appointmentData.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const endTime = new Date(e.appointmentData.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      // Crear el contenido personalizado
      appointmentContent.innerHTML = `
        <div>${e.appointmentData.title}</div>
        <div>${startTime} - ${endTime}</div>
      `;
    }
  };

  // Manejador para mostrar tooltip al pasar el mouse por una cita
  const onAppointmentTooltipShowing = (e: any) => {
    e.cancel = true; // Cancelar el tooltip por defecto

    // Si ya hay un tooltip visible, cerrarlo primero
    if (tooltipVisible) {
      closeTooltip();
    }

    if (e.appointments && e.appointments.length > 0) {
      // Usar setTimeout para evitar conflictos con el cierre anterior
      setTimeout(() => {
        setTooltipData(e.appointments[0].appointmentData);
        setTooltipTarget(e.targetElement);
        setTooltipVisible(true);
      }, 50);
    }
  };

  // Interceptar apertura del formulario predeterminado
  const handleAppointmentFormOpening = (e: any) => {
    e.cancel = true; // Cancelar formulario por defecto

    // Cerrar tooltip si está abierto
    closeTooltip();

    const appointmentData = e.appointmentData;
    const isNewAppointment = e.appointmentData?.id === undefined;

    onAppointmentFormOpening(appointmentData, isNewAppointment);
  };

  // Handler para cambios de vista o fecha
  const handleOptionChanged = (e: any) => {
    // Trackear cambios de vista
    if (e.name === 'currentView') {
      setCurrentView(e.value);
    }
    
    if (onViewOrDateChange && (e.name === 'currentView' || e.name === 'currentDate')) {
      const instance = e.component;
      // Esperar a que el Scheduler actualice la vista
      setTimeout(() => {
        if (instance.getStartViewDate && instance.getEndViewDate) {
          const start = instance.getStartViewDate();
          const end = instance.getEndViewDate();
          onViewOrDateChange(start, end);
        }
      }, 0);
    }
  };

  return (
    <div className='scheduler-wrapper' ref={schedulerWrapperRef}>
      {(isMedium || isLarge) && (
        <div ref={searchToolbarRef}>
          <SearchToolbar searchText={searchText} onSearchChange={setSearchText} searchFilter={searchFilter} onSearchFilterChange={setSearchFilter} onClearSearch={handleClearSearch} appointmentsCount={filteredAppointments.length} />
        </div>
      )}

      <Scheduler
        ref={ref}
        timeZone='America/Bogota'
        dataSource={filteredAppointments}
        defaultCurrentView='week'
        currentDate={currentDate}
        height={schedulerHeight}
        startDayHour={startDayHour}
        endDayHour={endDayHour}
        cellDuration={cellDuration}
        showAllDayPanel={false}
        customizeDateNavigatorText={(info) => formatDateWithDay(info.startDate, currentView, isSmall || isXSmall)}
        onAppointmentRendered={onAppointmentRendered}
        onAppointmentFormOpening={handleAppointmentFormOpening}
        onAppointmentTooltipShowing={onAppointmentTooltipShowing}
        onOptionChanged={handleOptionChanged}
        onAppointmentUpdated={onAppointmentUpdated}
        firstDayOfWeek={1} // Lunes
      >
        <View name='Día' type='day' intervalCount={1} />
        <View name='Semana laboral' type='workWeek' intervalCount={1} startDayHour={startDayHour} endDayHour={endDayHour} />
        <View name='Semana' type='week' intervalCount={1} />
        <View name='Mes' type='month' intervalCount={1} />
      </Scheduler>

      <Tooltip ref={tooltipRef} target={tooltipTarget} visible={tooltipVisible} position='top' hideOnOutsideClick={true} onHiding={closeTooltip} hideEvent='dxclick' showEvent=''>
        <AppointmentTooltipContent data={tooltipData} onClose={closeTooltip} />
      </Tooltip>
    </div>
  );
});

export default AppointmentScheduler;
